import {
    IconChipDepartment,
    IconChipMultiplePerson, IconDepartmentTitlePlus, IconKitchenTickets, IconKitchenTitlePlus,
    IconMealBowl,
    IconPersonPlusCard
} from "../../../../../components/buttons/IconButtons";
import { permissionObj } from "../../../../../Helpers/permission";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 *
 * Foodie shift access model. Mirrors the attendance ShiftAccess Models, swapping
 * the DEVICES resource for CAFETERIAS (diners). Drives the split create/update
 * flow (stepper + sections + summary) and the per-resource Add/Assigned tab
 * panels.
 */

/**
 * Resource type identifiers handled by the foodie shift sections.
 * Each section manages a single resource type (client-side selection).
 */
export const FOODIE_SHIFT_RESOURCE = {
    CAFETERIAS: 'CAFETERIAS',
    DEPARTMENTS: 'DEPARTMENTS',
    USERS: 'USERS'
}

/**
 * Backend "resourceString" selector for the assigned / not-assigned foodie shift
 * resource endpoints. The server uses it to pick the per-mode shape.
 */
export const FOODIE_SHIFT_RESOURCE_MODE = {
    [FOODIE_SHIFT_RESOURCE.CAFETERIAS]: 'cafeteria',
    [FOODIE_SHIFT_RESOURCE.DEPARTMENTS]: 'departments',
    [FOODIE_SHIFT_RESOURCE.USERS]: 'user'
}

export const FOODIE_SHIFT_MODEL = {
    id: null,
    name: '',
    isValid: function (cafeterias = []) {
        return this.name !== '' && cafeterias.length > 0;
    },
    toRegister: function (cafeterias, departments, user) {
        return {
            id: this.id,
            name: this.name,
            foodieCafeteriaIds: cafeterias.map(item => item.id),
            departmentIds: departments.map(item => item.id),
            userIds: user.map(item => item.userId ?? item.id)
        }
    }
}

export const STEPS_CREATE_FOODIE_SHIFT = {
    DINERS: {
        INDEX: 0,
        TRANS: "diners",
    },
    DEPARTMENTS: {
        INDEX: 1,
        TRANS: "departments",
    },
    CUSTOM_ACCESS: {
        INDEX: 2,
        TRANS: "custom_access",
    },
    SUMMARY: {
        INDEX: 3,
        TRANS: "summary",
    }
}

export const STEPS_UPDATE_FOODIE_SHIFT = {
    DINERS: {
        INDEX: 0,
        TRANS: "diners",
    },
    DEPARTMENTS: {
        INDEX: 1,
        TRANS: "departments",
    },
    CUSTOM_ACCESS: {
        INDEX: 2,
        TRANS: "custom_access",
    }
}

/**
 * Builds the inner Add/Assigned tab list for a section.
 *
 * On create (no id) only the Add tab (key 0) is shown; on update (id present)
 * the Assigned tab (key 1) is added too.
 *
 * @param {Object} tabs - Tab config object.
 * @returns {(id: number|string|null|undefined) => Array<Object>} Resolver.
 */
const getTabsById = (tabs) => (id) => {
    const hasId = id !== null && id !== undefined;
    return Object.values(tabs)
        .filter(e => typeof e === 'object' && e.key !== undefined)
        .filter(e => hasId || e.key === 0);
};

export const DINERS_TABS = {
    ADD_DINERS: {
        key: 0,
        name: "ADD_DINERS",
        translation: "add_diners",
        icon: <IconKitchenTitlePlus width={24} height={24}/>,
        permission: permissionObj?.WEB_FOODIE_SHIFT_CREATE,
    },
    ASSIGNED_DINERS: {
        key: 1,
        name: "ASSIGNED_DINERS",
        translation: "assigned_diners",
        icon: <IconKitchenTickets width={24} height={24}/>,
        permission: permissionObj?.WEB_FOODIE_SHIFT_CREATE,
    },
    getTabsById: getTabsById({}),
}
DINERS_TABS.getTabsById = getTabsById(DINERS_TABS);

export const FOODIE_DEPARTMENTS_TABS = {
    ADD_DEPARTMENT: {
        key: 0,
        name: "ADD_DEPARTMENT",
        translation: "add_department",
        icon: <IconDepartmentTitlePlus width={24} height={24}/>,
        permission: permissionObj?.WEB_FOODIE_SHIFT_CREATE,
    },
    ASSIGNED_DEPARTMENT: {
        key: 1,
        name: "ASSIGNED_DEPARTMENT",
        translation: "assigned_department",
        icon: <IconChipDepartment width={24} height={24}/>,
        permission: permissionObj?.WEB_FOODIE_SHIFT_CREATE,
    },
    getTabsById: getTabsById({}),
}
FOODIE_DEPARTMENTS_TABS.getTabsById = getTabsById(FOODIE_DEPARTMENTS_TABS);

export const FOODIE_CUSTOM_ACCESS_TABS = {
    ADD_EMPLOYEES: {
        key: 0,
        name: "ADD_EMPLOYEES",
        translation: "add_employees",
        icon: <IconPersonPlusCard width={24} height={24}/>,
        permission: permissionObj?.WEB_FOODIE_SHIFT_CREATE,
    },
    ASSIGNED_EMPLOYEES: {
        key: 1,
        name: "ASSIGNED_EMPLOYEES",
        translation: "assigned_employees",
        icon: <IconChipMultiplePerson width={24} height={24}/>,
        permission: permissionObj?.WEB_FOODIE_SHIFT_CREATE,
    },
    getTabsById: getTabsById({}),
}
FOODIE_CUSTOM_ACCESS_TABS.getTabsById = getTabsById(FOODIE_CUSTOM_ACCESS_TABS);

export const FOODIE_MEAL_TIME_MODEL = {
    id: null,
    name: '',
    startTime: '',
    endTime: '',
    cost: '',
    isValid: function (showCostInfo) {
        return this.name !== '' && this.startTime != null && this.startTime !== ''
            && this.endTime != null && this.endTime !== '' &&
            (!showCostInfo || (showCostInfo && this.cost !== null && this.cost !== ''));
    },
    toAddEdit: function () {
        return {
            id: this.id,
            name: this.name,
            startTime: this.startTime,
            endTime: this.endTime,
            cost: this.cost
        }
    }
}

export const FOODIE_ACCESS_MODEL = {
    id: null,
    assignedToId: null,
    requestedById: null,
    foodieCafeteriaId: null,
    fromTime: null,
    toTime: null,
    description: null,
    isValid: function () {
        return this.foodieCafeteriaId != null
            && this.fromTime != null
            && this.toTime != null
            && this.description !== null
            && this.description !== undefined
            && this.description.trim() !== '';
    },
    toRegister: function (assignedToId) {
        return {
            id: this.id,
            assignedToId: assignedToId,
            requestedById: this.requestedById,
            foodieCafeteriaId: this.foodieCafeteriaId,
            fromTime: new Date(this.fromTime).getTime(),
            toTime: new Date(this.toTime).getTime(),
            description: this.description
        }
    }
}
